import {
  type CreateTreatmentPlanTemplateRequest,
  type InterventionGroupTemplateRequest,
  type InterventionGroupTemplateResponse,
  type InterventionTemplateRequest,
  type InterventionTemplateResponse,
  type TreatmentPlanTemplateResponse,
  type TreatmentWeekTemplateRequest,
  type UpdateTreatmentPlanTemplateRequest,
  GroupBarSize,
  PatientLocationType
} from '../../api/generated';
import { TEMPLATE_COLOR_PALETTE } from '../../constants/templateColors';

export interface DraftGroup {
  _key: string;
  group: InterventionGroupTemplateResponse;
}

export interface DraftWeek {
  _key: string;
  groups: DraftGroup[];
}

export interface TreatmentPlanTemplateDraft {
  id?: string;
  name: string;
  strokeColor: string;
  fillColor: string;
  highlightColor: string;
  locationIds: string[];
  global: boolean;
  // Paid once per patient when their treatment on this plan ends; undefined = no completion amount.
  completionReimbursementAmount?: number;
  weeks: DraftWeek[];
}

function makeDraftGroup(group: InterventionGroupTemplateResponse): DraftGroup {
  return { _key: crypto.randomUUID(), group };
}

function makeDraftWeek(week: TreatmentPlanTemplateResponse['weeks'][number]): DraftWeek {
  return {
    _key: crypto.randomUUID(),
    groups: week.groups.map(makeDraftGroup)
  };
}

export function blankDraft(): TreatmentPlanTemplateDraft {
  const palette = TEMPLATE_COLOR_PALETTE[0];
  return {
    name: '',
    strokeColor: palette.strokeColor,
    fillColor: palette.fillColor,
    highlightColor: palette.highlightColor,
    locationIds: [],
    global: false,
    completionReimbursementAmount: undefined,
    weeks: []
  };
}

export function draftFromResponse(r: TreatmentPlanTemplateResponse): TreatmentPlanTemplateDraft {
  return {
    id: r.id,
    name: r.name,
    strokeColor: r.strokeColor,
    fillColor: r.fillColor,
    highlightColor: r.highlightColor,
    locationIds: [...r.locationIds],
    global: r.global,
    completionReimbursementAmount: r.completionReimbursementAmount,
    weeks: r.weeks.map(makeDraftWeek)
  };
}

export function blankDraftGroup(): DraftGroup {
  return makeDraftGroup({
    id: crypto.randomUUID(),
    name: '',
    icon: undefined,
    order: 0,
    barSize: GroupBarSize.Medium,
    reimbursementAmount: undefined,
    patientType: PatientLocationType.Admitted,
    interventions: [],
    placementRules: []
  });
}

export function blankDraftWeek(): DraftWeek {
  return { _key: crypto.randomUUID(), groups: [] };
}

function interventionToCloneRequest(
  iv: InterventionTemplateResponse
): InterventionTemplateRequest {
  return {
    id: iv.id,
    name: iv.name,
    description: iv.description,
    color: iv.color,
    icon: iv.icon,
    durationMinutes: iv.durationMinutes,
    sessionType: iv.sessionType,
    mandatoryStaffCompetenceRequirements: iv.mandatoryStaffCompetenceRequirements,
    flexStaffCompetenceRequirements: iv.flexStaffCompetenceRequirements,
    allowedDays: iv.allowedDays,
    roomRequirements: iv.roomRequirements
  };
}

function groupToRequest(
  group: InterventionGroupTemplateResponse,
  order: number
): InterventionGroupTemplateRequest {
  return {
    id: group.id,
    name: group.name,
    icon: group.icon,
    order,
    barSize: group.barSize,
    reimbursementAmount: group.reimbursementAmount,
    patientType: group.patientType,
    interventions: group.interventions.map(interventionToCloneRequest),
    placementRules: group.placementRules
  };
}

function weekToRequest(week: DraftWeek, weekNumber: number): TreatmentWeekTemplateRequest {
  return {
    weekNumber,
    groups: week.groups.map((dg, idx) => groupToRequest(dg.group, idx))
  };
}

export function draftToCreateRequest(
  d: TreatmentPlanTemplateDraft
): CreateTreatmentPlanTemplateRequest {
  return {
    name: d.name,
    strokeColor: d.strokeColor,
    fillColor: d.fillColor,
    highlightColor: d.highlightColor,
    locationIds: d.locationIds,
    global: d.global,
    completionReimbursementAmount: d.completionReimbursementAmount,
    weeks: d.weeks.map((w, idx) => weekToRequest(w, idx + 1))
  };
}

export function draftToUpdateRequest(
  d: TreatmentPlanTemplateDraft
): UpdateTreatmentPlanTemplateRequest {
  return {
    name: d.name,
    strokeColor: d.strokeColor,
    fillColor: d.fillColor,
    highlightColor: d.highlightColor,
    locationIds: d.locationIds,
    global: d.global,
    completionReimbursementAmount: d.completionReimbursementAmount,
    weeks: d.weeks.map((w, idx) => weekToRequest(w, idx + 1))
  };
}
